import React, { useEffect, useRef, useState } from 'react';
import { teamMembers } from '../mock';
import { Mail, Phone } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';

const studioValues = [
  {
    title: "Integrity",
    description: "We approach every project with honesty and transparency, ensuring our designs truly serve our clients' needs."
  },
  {
    title: "Innovation",
    description: "We embrace new ideas and technologies while respecting timeless design principles that have proven their worth."
  },
  {
    title: "Collaboration",
    description: "We work closely with clients, consultants, and craftspeople to bring the best possible outcome to every project."
  },
  {
    title: "Sustainability",
    description: "We design with the future in mind, creating homes that are efficient, durable, and environmentally responsible."
  }
];

const studioCapabilities = [
  "Residential Architecture",
  "Interior Design",
  "Heritage Renovations",
  "New Home Construction",
  "Sustainable Design",
  "Landscape Design",
  "Project Management",
  "Town Planning Permit Applications",
  "Building Permit Documentation",
  "3D Visualisation & Architectural Rendering"
];

export const Studio = ({ showHero = false, showTeam = false, darkCapabilities = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="studio-wrapper">
      {/* Hero Section - Only shown on dedicated studio page */}
      {showHero && (
        <section className="studio-hero">
          <div className="studio-hero-overlay"></div>
          <div className="studio-hero-content">
            <h1 className="studio-hero-title">Our Studio</h1>
            <p className="studio-hero-subtitle">Creating spaces that inspire and endure</p>
          </div>
        </section>
      )}

      {/* Our Approach Section - Dark background to blend with hero on studio page */}
      <section className={`home-section ${showHero ? 'home-section-dark studio-approach-blend' : 'home-section-cream'}`} ref={sectionRef}>
        <div className="home-section-inner">
          <div className="home-section-header">
            <h2 className={`section-title-inline ${showHero ? 'title-light-text' : ''}`}>
              <span className="title-light">OUR</span> <span className="title-bold">APPROACH.</span>
            </h2>
          </div>
          <div className={`studio-content-grid ${isVisible ? 'content-visible' : ''}`}>
            <div className={`studio-text-block ${showHero ? 'text-light-block' : ''}`}>
              <p className={`studio-text ${showHero ? 'text-light' : ''}`}>
                At Domus Living, we believe in creating spaces that enhance daily life through thoughtful design. 
                Our approach is rooted in understanding our clients&apos; needs, respecting the context of each site, 
                and crafting homes that stand the test of time.
              </p>
              <p className={`studio-text ${showHero ? 'text-light' : ''}`}>
                We focus on materiality, light, and the relationship between interior and exterior spaces 
                to create residences that feel both contemporary and timeless.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="home-section home-section-gray">
        <ScrollReveal>
          <div className="home-section-inner">
            <div className="home-section-header">
              <h2 className="section-title-inline">
                <span className="title-light">OUR</span> <span className="title-bold">VALUES.</span>
              </h2>
            </div>
            <div className="studio-values-grid">
              {studioValues.map((value, index) => (
                <div key={index} className="studio-value-card">
                  <h3 className="studio-value-title">{value.title}</h3>
                  <p className="studio-value-text">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Capabilities Section */}
      <section className={`home-section ${darkCapabilities ? 'home-section-dark' : 'home-section-cream'}`}>
        <ScrollReveal>
          <div className="home-section-inner">
            <div className="home-section-header">
              <h2 className={`section-title-inline ${darkCapabilities ? 'title-light-text' : ''}`}>
                <span className="title-light">OUR</span> <span className="title-bold">CAPABILITIES.</span>
              </h2>
            </div>
            <div className="studio-capabilities-content">
              <p className={`studio-capabilities-intro ${darkCapabilities ? 'text-light' : ''}`}>
                Our multidisciplinary team brings together expertise across all aspects of residential design and construction.
              </p>
              <div className="studio-capabilities-grid">
                {studioCapabilities.map((capability, index) => (
                  <div key={index} className={`studio-capability-item ${darkCapabilities ? 'capability-light' : ''}`}>
                    {capability}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Team Section - Only shown on dedicated studio page */}
      {showTeam && (
        <section className="home-section home-section-cream">
          <ScrollReveal>
            <div className="home-section-inner">
              <div className="home-section-header">
                <h2 className="section-title-inline">
                  <span className="title-light">OUR</span> <span className="title-bold">TEAM.</span>
                </h2>
              </div>
              <div className="studio-team-grid">
                {teamMembers.map((member) => (
                  <div key={member.id} className="studio-team-card">
                    <h3 className="studio-team-name">{member.name}</h3>
                    <p className="studio-team-role">{member.role}</p>
                    <div className="studio-team-contact">
                      <a href={`tel:${member.phone}`} className="studio-contact-link">
                        <Phone size={14} />
                        <span>{member.phone}</span>
                      </a>
                      <a href={`mailto:${member.email}`} className="studio-contact-link">
                        <Mail size={14} />
                        <span>{member.email}</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </section>
      )}
    </div>
  );
};
